import { useState } from 'react';
import { type WorkQueueCase } from '../../../../services/workQueue';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { reportApprovalActionFailure } from './reportApprovalActionFailure';
import { isValidReason } from './reasonLimits';

interface ReasonActionConfig {
    run: (patientId: string, reason: string) => Promise<void>;
    successToast: string;
    fallbackMessage: string;
    source: string;
    action: string;
}

export function useReasonActionSubmit(
    c: WorkQueueCase,
    onSuccess: (actionId: string) => void,
    { run, successToast, fallbackMessage, source, action }: ReasonActionConfig
) {
    const { showToast } = useToast();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const canSubmit = isValidReason(reason) && !submitting;

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            await run(c.id, reason.trim());
            showToast(successToast, 'success');
            onSuccess(action);
        } catch (err) {
            const message = apiErrorMessage(err, fallbackMessage);
            showToast(message, 'error');
            reportApprovalActionFailure(source, action, c.id, message, err);
            setSubmitting(false);
        }
    };

    const confirm = (): void => {
        if (!canSubmit) return;
        void submit();
    };

    return { reason, setReason, submitting, canSubmit, confirm };
}
